import { type KeyboardEvent, useRef, useState } from 'react'
import { ArrowUpDown, ChevronDown, Circle } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { cn } from '@/utils'

export type TraySortOption<T extends string> = { value: T; label: string }

export type TraySortButtonProps<T extends string> = {
  value: T
  options: ReadonlyArray<TraySortOption<T>>
  onValueChange: (value: T) => void
  /** Accessible name of the trigger, e.g. "Sort photos". */
  ariaLabel: string
  /** Accessible name of the option list, e.g. "Sort photos by". */
  listboxLabel: string
  /** Reports open/close so a host panel can track "is one of my dropdowns open" (e.g. for an
   *  Escape-key priority chain) without owning this component's internal state. */
  onOpenChange?: (open: boolean) => void
}

/**
 * "Sort ▾" trigger + popover listbox of radio-style options; closes on pick.
 *
 * Keyboard: opening (Enter/Space on the trigger) moves focus to the first option; Up/Down move
 * between options (wrapping), Home/End jump to the ends; Enter/Space picks the focused option.
 * Picking an option or pressing Escape closes the list and returns focus to the trigger. Clicking
 * outside closes it without pulling focus back, so focus can land on whatever was clicked.
 */
export function TraySortButton<T extends string>({
  value,
  options,
  onValueChange,
  ariaLabel,
  listboxLabel,
  onOpenChange,
}: TraySortButtonProps<T>) {
  const [open, setOpen] = useState(false)
  // Roving tabindex: only the active option is in the tab order, the rest are reached by arrows.
  const [activeIndex, setActiveIndex] = useState(0)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])
  // Set by a pick or Escape — the closes that should hand focus back to the trigger.
  const returnFocusOnCloseRef = useRef(false)

  function handleOpenChange(next: boolean) {
    if (next) setActiveIndex(0)
    setOpen(next)
    onOpenChange?.(next)
  }

  function focusOption(index: number) {
    const count = options.length

    if (count === 0) return

    const wrapped = (index + count) % count

    setActiveIndex(wrapped)
    optionRefs.current[wrapped]?.focus()
  }

  function handleListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusOption(activeIndex + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusOption(activeIndex - 1)
        break
      case 'Home':
        event.preventDefault()
        focusOption(0)
        break
      case 'End':
        event.preventDefault()
        focusOption(options.length - 1)
        break
      default:
        break
    }
  }

  function pick(next: T) {
    returnFocusOnCloseRef.current = true
    onValueChange(next)
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-2 px-4"
          aria-label={ariaLabel}
          aria-expanded={open}
        >
          <ArrowUpDown className="size-4" aria-hidden="true" />
          Sort
          <ChevronDown className="size-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="z-[60] w-44 p-1"
        sideOffset={4}
        onOpenAutoFocus={(event) => {
          // Land on the first option explicitly rather than on whatever Radix finds first.
          event.preventDefault()
          optionRefs.current[0]?.focus()
        }}
        onEscapeKeyDown={() => {
          returnFocusOnCloseRef.current = true
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (returnFocusOnCloseRef.current) triggerRef.current?.focus()
          returnFocusOnCloseRef.current = false
        }}
      >
        <div role="listbox" aria-label={listboxLabel} onKeyDown={handleListKeyDown}>
          {options.map((option, index) => {
            const selected = value === option.value

            return (
              <button
                key={option.value}
                ref={(el) => {
                  optionRefs.current[index] = el
                }}
                type="button"
                role="option"
                aria-selected={selected}
                tabIndex={index === activeIndex ? 0 : -1}
                className={cn(
                  'relative flex w-full items-center rounded-sm py-1.5 pl-8 pr-2 text-left text-sm outline-none',
                  'hover:bg-accent hover:text-accent-foreground',
                  // Keyboard focus must be visible — it was `outline-none` with no replacement.
                  'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring'
                )}
                onFocus={() => setActiveIndex(index)}
                onClick={() => pick(option.value)}
              >
                {selected ? (
                  <Circle
                    className="absolute left-3 top-1/2 size-2 -translate-y-1/2 fill-current text-foreground"
                    aria-hidden="true"
                  />
                ) : null}
                <span>{option.label}</span>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
